/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useRef, useState} from 'react';
import {
  Button,
  ButtonGroup,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from '@material-ui/core';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import {DeviceFile, DownloadFileTypes, PlatformDeviceType} from '@ion/core/src/types/entities';
import ClickAwayListener from '../components/listeners/ClickAwayListener';
import {canDownloadDeviceFiles} from '../editors/helpers';
import {DropdownArrowIcon} from '../icons/DropdownArrowIcon';
import {dlsZIndex} from '../theme/theme';

export interface DownloadFileViewProps {
  deviceFile: DeviceFile;
  deviceTypes: PlatformDeviceType;
  downloadFile?: Function;
  releaseNote?: Function;
  downloadFileclass?: string;
  downloadFileButtonClass?: string;
  hasUpdatePermission?: boolean;
}
export const lmuPlatform = 'LMU CONFIG';

enum DEVICE_FILE_DOWNLOAD {
  CSV_FILE = 0,
  XML_FILE = 1,
  RELEASE_NOTES = 2
}

const getOptions = (showDownloadCSV: boolean) => {
  const options = [translate('devicefiles:editors.downloadFiles.download')];
  if (showDownloadCSV) {
    options.push(translate('devicefiles:editors.downloadFiles.deviceFileCSV'), translate('devicefiles:editors.downloadFiles.deviceFileXML'));
  } else {
    options.push(translate('devicefiles:editors.downloadFiles.deviceFile'));
  }
  options.push(translate('devicefiles:editors.downloadFiles.releaseNote'));

  return options;
};

export const checkShowDownloadCSV = (deviceFile: DeviceFile): boolean => (
  deviceFile?.validationMetaData?.DataUrlCsv &&
isEqual((deviceFile?.validationMetaData?.platformName).toUpperCase(), lmuPlatform)
);

/**
 * The DownloadFileView provides a list of Download File type values for display
 * within a dropdown list.
 *
 * ```typescript
 *
 * import {DownloadFileView} from '@ion/dls/src/editors/';
 *
 * <DownloadFileView deviceFile{deviceFile}
 *   deviceTypes={deviceTypes} />
 *
 * ```
 *
 */
export const DownloadFileView = ({
  deviceFile,
  deviceTypes,
  downloadFile,
  releaseNote,
  downloadFileclass,
  downloadFileButtonClass,
  hasUpdatePermission = false
}: DownloadFileViewProps) => {
  const ref                               = useRef(null);
  const [open, setOpen]                   = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const showDownloadCSV = checkShowDownloadCSV(deviceFile);
  const OPTIONS         = getOptions(showDownloadCSV);

  const handleMenuItemClick = useCallback(function(e, index) {
    setSelectedIndex(index);
    setOpen(false);
    if (index === DEVICE_FILE_DOWNLOAD.CSV_FILE) {
      downloadFile(showDownloadCSV ? DownloadFileTypes.CSV : '');
    } else if (index === DEVICE_FILE_DOWNLOAD.XML_FILE) {
      if (showDownloadCSV) {
        downloadFile(DownloadFileTypes.XML);
      } else {
        releaseNote();
      }
    } else if (index === DEVICE_FILE_DOWNLOAD.RELEASE_NOTES) {
      releaseNote();
    }
  }, [downloadFile, showDownloadCSV, releaseNote]);

  const handleToggle = useCallback(function() {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const handleClose = useCallback(function({target}) {
    if (!ref.current?.contains(target)) {
      setOpen(false);
    }
  }, []);

  const checkEnableDisableBtn = (index) => {
    if (!hasUpdatePermission) {
      return true;
    } if (index === DEVICE_FILE_DOWNLOAD.CSV_FILE) {
      return !canDownloadDeviceFiles(deviceFile, deviceTypes);
    } else if (index === DEVICE_FILE_DOWNLOAD.XML_FILE) {
      if (showDownloadCSV) {
        return !canDownloadDeviceFiles(deviceFile, deviceTypes);
      } else {
        return !isEqual(deviceFile?.validationMetaData.releaseNoteAttached, true);
      }
    } else if (index === DEVICE_FILE_DOWNLOAD.RELEASE_NOTES) {
      return !isEqual(deviceFile?.validationMetaData?.releaseNoteAttached, true);
    }
  };

  const hideDownloadButton = () => (
    hasUpdatePermission
      ? !(checkEnableDisableBtn(DEVICE_FILE_DOWNLOAD.CSV_FILE) &&
      checkEnableDisableBtn(DEVICE_FILE_DOWNLOAD.XML_FILE) &&
      checkEnableDisableBtn(DEVICE_FILE_DOWNLOAD.RELEASE_NOTES)) : false
  );

  if (hideDownloadButton()) {
    return (
      <>
        <ButtonGroup ref={ref}>
          <div className={downloadFileclass}>
            <Button className={downloadFileButtonClass}>
              {OPTIONS[0]}
            </Button>
            <Button size='small'
              aria-controls={open}
              aria-expanded={open}
              aria-haspopup='menu'
              onClick={handleToggle}>
              <DropdownArrowIcon />
            </Button>
          </div>
        </ButtonGroup>
        <Popper sx={dlsZIndex('tableViewToggleButton')}
          open={open}
          anchorEl={ref.current}
          transition
          placement='top-start'
          disablePortal>
          {({TransitionProps, placement}) => (
            <Grow {...TransitionProps}
              style={{
                transformOrigin : placement === 'bottom' ? 'center top' : 'center bottom'
              }}>
              <Paper>
                <ClickAwayListener onClickAway={handleClose}
                  mountListener>
                  <MenuList autoFocusItem>
                    {OPTIONS.slice(1).map((option, index) => (
                      <MenuItem key={option}
                        disabled={checkEnableDisableBtn(index)}
                        selected={index === selectedIndex}
                        onClick={function(e) { handleMenuItemClick(e, index); }}>
                        {option}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    );
  }
};
export default React.memo(DownloadFileView);
